import { z } from 'zod';

/**
 * URL search-parameter schema for the `/settings` route. Captures:
 *   - `section`  → which anchor is active (drives scroll-restore + sticky nav)
 *   - `workspaceId` → which workspace we're acting on (members, invites, quotas, delete)
 *   - `dialog`   → which modal is open (one at a time)
 *
 * Everything else (form drafts, invite email/role, quota draft map, etc.)
 * stays in component-local `useState` because cramming a form into the URL
 * is terrible UX (a refresh wipes drafts, every keystroke creates a history
 * entry, copy-paste leaks half-typed values).
 */
export const SETTINGS_SECTIONS = [
  'profile',
  'company',
  'people',
  'general',
  'workspaces',
  'access',
  'notifications',
  'security',
  'danger',
] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

/**
 * Deprecated section ids kept in the search schema *only* so an old link
 * still parses as that literal (rather than `.catch(undefined)`-ing to the
 * enum's fallback) and `settings.tsx`'s `beforeLoad` can redirect it:
 * `templates` / `api-keys` → `/templates` / `/api-keys` (moved off
 * `/settings` entirely); `team` → `?section=access`, `organization` →
 * `?section=company`, `org-members` → `?section=people` (all three renamed
 * in place: "Team" → "Workspace access", "Organization" → "Company",
 * "Organization Members" → "People"). Never rendered — absent from
 * `SettingsRail` and `SettingsPage`'s section switch. Partner integration
 * never had its own `section` value (it rode inside `api-keys`), so this
 * redirect list covers it too.
 */
export const DEPRECATED_SETTINGS_SECTIONS = [
  'templates',
  'api-keys',
  'team',
  'organization',
  'org-members',
] as const;
export type DeprecatedSettingsSection = (typeof DEPRECATED_SETTINGS_SECTIONS)[number];

export const SETTINGS_DIALOGS = [
  'new-workspace',
  'delete-workspace',
  'create-api-key',
  'new-collection',
] as const;
export type SettingsDialog = (typeof SETTINGS_DIALOGS)[number];

export const settingsSearchSchema = z.object({
  // `.catch(undefined)` so a stale/removed section (e.g. an old
  // `?section=preferences` bookmark after that tab was removed) degrades to
  // `undefined` → the nav hook's `?? 'profile'` default, instead of throwing a
  // ZodError out of `validateSearch`. `templates` / `api-keys` parse into the
  // deprecated union below instead of falling into this one, since
  // `beforeLoad` needs the literal value to redirect them.
  section: z
    .enum([...SETTINGS_SECTIONS, ...DEPRECATED_SETTINGS_SECTIONS])
    .optional()
    .catch(undefined),
  workspaceId: z.coerce.number().int().optional(),
  // Target of the delete-workspace confirm. Kept separate from `workspaceId` so
  // opening the destructive confirm on a non-current row does NOT re-point the
  // settings page's "current workspace" view behind the modal.
  deleteWorkspaceId: z.coerce.number().int().optional(),
  dialog: z.enum(SETTINGS_DIALOGS).optional(),
});

export type SettingsSearch = z.infer<typeof settingsSearchSchema>;

/**
 * Narrows `search.section` (which also accepts the deprecated literals above)
 * down to a real, renderable section. `settings.tsx`'s `beforeLoad` redirects
 * a deprecated value away before the page ever mounts, but this is the
 * defensive fallback for a stale in-memory search object reaching the page
 * through a client-side nav that skips `beforeLoad` reconciliation.
 */
export function isSettingsSection(
  section: SettingsSection | DeprecatedSettingsSection | undefined,
): section is SettingsSection {
  return (SETTINGS_SECTIONS as readonly string[]).includes(section ?? '');
}
